import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useRouterState } from "@tanstack/react-router";
import {
  Account,
  APIError,
  AuthConfig,
  BehavesAsControlCodeMappings,
  ControlCode,
  ControlCodes,
  cn,
  controlOptions,
  flaggedControls,
  getControlName,
  getEnumName,
  groupBy,
  isDataControl,
  isDefensiveControl,
  isDiscoveredDevicesControl,
  isEmailControl,
  isEndpointManagementControl,
  isHostFirewallControl,
  isIdentityProviderControl,
  isPrivateRepoControl,
  isVulnerabilityManagementControl,
  ObservedDetected,
  pluralize,
  queries,
  StatusResponse,
  service,
  useAuthConfig,
  ValidControlCode,
} from "@theprelude/core";
import {
  BookIcon,
  Button,
  ButtonProps,
  CheckmarkIcon,
  CloseIcon,
  ControlIcon,
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DiscussIcon,
  GridIcon,
  Input,
  MicrosoftIconPlain,
  notifyError,
  notifySuccess,
  PlusIcon,
  RefreshIcon,
  SearchIcon,
  SettingsIcon,
  TableIcon,
  TerminalIcon,
  ToggleGroup,
  ToggleItem,
  UnlinkIcon,
} from "@theprelude/ds";
import { useRef, useState } from "react";
import { setPageTitle } from "@/components/app-shell/app-shell";
import { DefaultTooltip } from "@/components/default-tooltip";
import { DropdownSelect } from "@/components/dropdown-select";
import { Flag, hasFeatures } from "@/components/flag/flag";
import { Highlight } from "@/components/highlight";
import { EditIntegrationDropdown } from "@/components/integrations/edit-integration-dropdown";
import { getIntegrationForm } from "@/components/integrations/integration-overlay";
import { useOverlay } from "@/hooks/use-overlay";
import { createQuery } from "@/lib/create-query";
import { fuzzyFilter } from "@/lib/fuzzy-filters";
import { tb } from "@/lib/tracking";

export const Route = createFileRoute("/_app/account/integrations/")({
  beforeLoad: () => setPageTitle("Integrations"),
  component: IntegrationsPage,
});

type Layout = "grid" | "table";

function IntegrationsPage() {
  const router = useRouterState();
  const authConfig = useAuthConfig();
  const overlay = useOverlay();

  const [layout, setLayout] = useState<Layout>("grid");
  const [searchText, setSearchText] = useState("");

  const { data: instances } = useSuspenseQuery({
    ...queries.iam.getAccount(authConfig),
    select: (account) =>
      account.controls.map((i) => ({
        control: i.id,
        instanceId: i.instance_id,
      })),
  });

  const { data: account } = useSuspenseQuery({
    ...queries.iam.getAccount(authConfig),
  });

  const hasDetectFeature = hasFeatures(account, ["detect"]);

  const syncAllIntegrations = useMutation({
    mutationFn: () => {
      const updatableControls = instances.filter(
        (i) => !isDataControl(i.control),
      );
      const scmService = service(authConfig).scm;

      const promises: Promise<StatusResponse | ObservedDetected>[] =
        updatableControls.map((i) => {
          return scmService.updatePolicy(i.control, i.instanceId);
        });

      if (hasDetectFeature) {
        promises.push(service(authConfig).partner.observedDetected());
      }

      return Promise.allSettled(promises);
    },
    onSuccess: () => {
      notifySuccess({ message: "Sync initiated." });
    },
    onError: (err) => {
      if (err instanceof APIError && err.status === 400) {
        notifyError({
          message: "Integrations sync already in progress.",
        });
      } else {
        notifyError({ message: "Failed to sync integrations.", error: err });
      }
    },
  });

  return (
    <div className="flex w-full flex-col gap-4 p-6">
      <div className={"flex min-h-[38px] items-center justify-between"}>
        <h1 className="min-h-[38px] text-2xl dark:font-light">Integrations</h1>
        <div className="flex items-center gap-2">
          <Link
            to={router.location.pathname}
            search={(prev) => ({ ...prev, overlay: ["event-log"] })}
            params={(prev) => prev}
          >
            <Button
              icon={<TerminalIcon className="text-secondary-foreground" />}
              intent="secondary"
              className={tb("event-log")}
            >
              <span className="hidden lg:block">Event log</span>
            </Button>
          </Link>
          <Button
            icon={<RefreshIcon className="text-secondary-foreground" />}
            className={tb("sync-all-integrations")}
            intent="secondary"
            onClick={() => syncAllIntegrations.mutate()}
            loading={syncAllIntegrations.isPending}
            disabled={syncAllIntegrations.isPending}
          >
            <span className="hidden lg:block">Sync all integrations</span>
          </Button>
          <AddIntegrationButton
            icon={<PlusIcon />}
            intent="primary"
            className={tb("add-an-integration")}
            onClick={(control) => overlay.open("integration", { control })}
          >
            <span className="hidden lg:block">Add an integration</span>
          </AddIntegrationButton>
        </div>
      </div>
      <div className="border-stroke-line border-b" />

      <div className="grid grid-cols-2 gap-2 xl:grid-cols-3">
        <SearchInput value={searchText} onChange={setSearchText} />
        <div className="order-3 col-span-2 flex justify-center xl:order-2 xl:col-span-1">
          <Button
            type="button"
            icon={<DiscussIcon className="size-4 text-secondary-foreground" />}
            aria-haspopup="dialog"
            className="hs-cta-trigger-button hs-cta-trigger-button-187015940223 flex flex-shrink-0 cursor-pointer items-center gap-2 bg-primary-background px-4 py-2 font-normal dark:bg-tertiary-foreground"
          >
            Don&apos;t see an integration you&apos;re looking for?{" "}
            <span className="underline">Suggest</span>
          </Button>
        </div>

        <div className="order-2 flex items-center justify-end gap-1 lg:gap-4 xl:order-3">
          <IntegrationCount />
          <ToggleGroup value={layout} onValueChange={setLayout}>
            <DefaultTooltip content="Display as table">
              <ToggleItem<Layout> value="table" icon={<TableIcon />} />
            </DefaultTooltip>
            <DefaultTooltip content="Display as grid">
              <ToggleItem<Layout> value="grid" icon={<GridIcon />} />
            </DefaultTooltip>
          </ToggleGroup>
        </div>
      </div>

      <Integrations layout={layout} searchText={searchText.trim()} />
    </div>
  );
}

type IntegrationRow = {
  id: string;
  control: ValidControlCode;
  controlName: string;
  name: string;
  connected: boolean;
  type: ReturnType<typeof getControlType>;
  hasGroups: boolean;
};

const AUTOMATED_SETUP_INTEGRATIONS = [
  {
    id: "microsoft-automated-setup",
    name: "Microsoft",
    description: "Includes Defender, Intune, 365, Entra ID",
    icon: ({ className }: { className?: string }) => (
      <MicrosoftIconPlain
        className={cn(className, "text-secondary-foreground")}
      />
    ),
    type: "Automated Setup & Optimization",
  },
] as const;

type AutomatedSetupRow = (typeof AUTOMATED_SETUP_INTEGRATIONS)[number];

const integrationsQuery = (authConfig: AuthConfig) =>
  createQuery({
    queries: [queries.iam.getAccount(authConfig)],
    queryFn: formatIntegrations,
  });

const getControlType = (control: ValidControlCode) => {
  if (isDefensiveControl(control)) {
    return "Defensive";
  }
  if (isDiscoveredDevicesControl(control)) {
    return "Device Discoverability";
  }
  if (isDataControl(control)) {
    return "Data";
  }
  if (isEndpointManagementControl(control)) {
    return "Endpoint Management";
  }
  if (isEmailControl(control)) {
    return "Email";
  }
  if (isHostFirewallControl(control)) {
    return "Host Firewall";
  }
  if (isIdentityProviderControl(control)) {
    return "Identity Provider";
  }
  if (isPrivateRepoControl(control)) {
    return "Private Repository";
  }
  if (isVulnerabilityManagementControl(control)) {
    return "Vulnerability Management";
  }

  return "";
};

const formatIntegrations = (account: Account) => {
  const integrations = controlOptions
    .filter((control) => {
      if (
        control.id === ControlCodes.CROWDSTRIKE_IDENTITY ||
        control.id === ControlCodes.INTUNE_HOST_FIREWALL
      ) {
        return false;
      }
      if (!getIntegrationForm(control.id)) {
        return false;
      }
      const flag = flaggedControls.find((f) => f.control === control.id)?.flag;
      if (!flag) return true;

      return hasFeatures(account, [flag]);
    })
    .flatMap((control): IntegrationRow | IntegrationRow[] => {
      const type = getControlType(control.id);
      const behavesAsControl =
        BehavesAsControlCodeMappings[control.id] ?? control.id;
      const connections = account.controls.filter(
        (c) => c.id === behavesAsControl,
      );
      const hasGroups = control.id === ControlCodes.ENTRA;

      if (connections.length > 0) {
        return connections.map(
          (connection): IntegrationRow => ({
            id: connection.instance_id,
            controlName: getControlName(behavesAsControl),
            name: connection.name?.trim() || "",
            control: behavesAsControl,
            connected: true,
            type,
            hasGroups,
          }),
        );
      }
      return {
        id: `${control.id}-new`,
        name: getControlName(behavesAsControl),
        controlName: getControlName(behavesAsControl),
        control: behavesAsControl,
        connected: false,
        type,
        hasGroups,
      };
    })
    .sort((a, b) => {
      const compare = a.controlName.localeCompare(b.controlName);
      if (compare !== 0) {
        return compare;
      }
      return a.name.localeCompare(b.name);
    });

  return integrations;
};

function SearchInput({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <div className="group h-[38px] w-80">
      <Input
        ref={inputRef}
        autoFocus
        value={value}
        onChange={(e) => onChange(e.target.value)}
        before={
          <SearchIcon className="ml-[2px] text-secondary-foreground group-hover:text-primary-foreground" />
        }
        placeholder="Search this view"
        className="m-0 flex h-[38px] items-center justify-center pl-8"
      />
    </div>
  );
}

function IntegrationCount() {
  const authConfig = useAuthConfig();
  const { data: integrations } =
    integrationsQuery(authConfig).useSuspenseQuery();

  const count = integrations.filter((i) => i.connected).length;

  return (
    <p className="hidden text-secondary-foreground md:block">{`${count} ${pluralize(
      count,
      "integration",
    )}`}</p>
  );
}

function Integrations({
  layout,
  searchText,
}: {
  layout: Layout;
  searchText: string;
}) {
  const authConfig = useAuthConfig();

  const { data: integrations } =
    integrationsQuery(authConfig).useSuspenseQuery();

  // Filter automated setup integrations based on search
  const filteredAutomatedSetup = AUTOMATED_SETUP_INTEGRATIONS.filter(
    (i) =>
      fuzzyFilter(i.name, searchText) || fuzzyFilter(i.description, searchText),
  );

  // Filter and categorize regular integrations
  const categorizedIntegrations = Object.entries(
    groupBy(
      integrations.filter(
        (i) =>
          fuzzyFilter(i.name, searchText) ||
          fuzzyFilter(i.controlName, searchText),
      ),
      (i) => i.type,
    ),
  ).sort((a, b) => a[0].localeCompare(b[0]));

  const hasResults =
    categorizedIntegrations.length > 0 || filteredAutomatedSetup.length > 0;

  return (
    <div className="flex flex-col gap-4">
      {!hasResults && (
        <div className="flex items-center justify-center p-8">
          You have not added any integrations
          {searchText && ", or your search term contains no matches"}.
        </div>
      )}

      <Flag features={["policy_evaluator"]}>
        {filteredAutomatedSetup.length > 0 && (
          <div className="flex flex-col gap-4">
            <div className="font-medium text-base">
              Automated Setup &amp; Optimization
            </div>
            <div
              className={
                layout === "grid"
                  ? "grid 3xl:grid-cols-6 grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4"
                  : undefined
              }
            >
              {filteredAutomatedSetup.map((integration, i) =>
                layout === "grid" ? (
                  <AutomatedSetupGridItem
                    key={integration.id}
                    integration={integration}
                    searchText={searchText}
                  />
                ) : (
                  <AutomatedSetupTableItem
                    key={integration.id}
                    integration={integration}
                    searchText={searchText}
                    isLast={i === filteredAutomatedSetup.length - 1}
                  />
                ),
              )}
            </div>
          </div>
        )}
      </Flag>

      {categorizedIntegrations.map(([category, categoryIntegrations]) => {
        if (categoryIntegrations?.length === 0) {
          return null;
        }

        return (
          <div key={category} className="flex flex-col gap-4">
            <div className="font-medium text-base">{category}</div>
            <div
              className={
                layout === "grid"
                  ? "grid 3xl:grid-cols-6 grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4"
                  : undefined
              }
            >
              {categoryIntegrations.map((integration, i) =>
                layout === "grid" ? (
                  <IntegrationGridItem
                    key={integration.id}
                    integration={integration}
                    searchText={searchText}
                  />
                ) : (
                  <IntegrationTableItem
                    key={integration.id}
                    integration={integration}
                    searchText={searchText}
                    isLast={i === categoryIntegrations.length - 1}
                  />
                ),
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function AutomatedSetupGridItem({
  integration,
  searchText,
}: {
  integration: AutomatedSetupRow;
  searchText: string;
}) {
  const overlay = useOverlay();
  return (
    <div className="flex flex-col gap-4 border border-stroke-table p-4 dark:border-stroke-line">
      <div className="flex flex-col gap-2 pb-4">
        <div className="flex justify-between">
          <integration.icon className="h-10 w-10 text-secondary-foreground" />
          <div className="size-4" />
        </div>
        <div className="flex flex-col flex-wrap items-start gap-2 lg:flex-row lg:items-center">
          <Highlight
            text={integration.name}
            highlight={searchText}
            className="text-xl"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Highlight
            text={integration.description}
            highlight={searchText}
            className="text-secondary-foreground text-sm"
          />
        </div>
      </div>
      <div className="mt-auto flex items-center gap-2 border-stroke-table border-t pt-4 dark:border-stroke-line">
        <Button
          icon={<SettingsIcon className="text-secondary-foreground" />}
          intent="pill"
          size="tiny"
          className={tb(`run-wizard-${integration.name.toLowerCase()}`)}
          onClick={() => {
            void overlay.open(integration.id);
          }}
        >
          Run connection wizard
        </Button>
      </div>
    </div>
  );
}

function AutomatedSetupTableItem({
  integration,
  searchText,
  isLast,
}: {
  integration: AutomatedSetupRow;
  searchText: string;
  isLast: boolean;
}) {
  const overlay = useOverlay();
  return (
    <div
      className={cn(
        "flex items-center justify-between gap-2 border border-stroke-table border-b-0 dark:border-stroke-line",
        isLast && "border-b",
      )}
    >
      <div className="flex w-full min-w-24 items-center gap-2 p-2">
        <integration.icon className="text-secondary-foreground" />
        <div className="flex flex-wrap items-baseline gap-2">
          <Highlight text={integration.name} highlight={searchText} />
          <Highlight
            text={integration.description}
            highlight={searchText}
            className="text-secondary-foreground text-xs"
          />
        </div>
      </div>
      <div className="flex w-full items-center justify-between gap-4 p-2">
        <div className="flex w-full flex-wrap items-center gap-4">
          <Button
            icon={<SettingsIcon className="text-secondary-foreground" />}
            intent="pill"
            size="tiny"
            className={tb(`run-wizard-${integration.name.toLowerCase()}`)}
            onClick={() => {
              void overlay.open("microsoft-automated-setup");
            }}
          >
            Run connection wizard
          </Button>
        </div>
        <div className="size-4" />
      </div>
    </div>
  );
}

function IntegrationTableItem({
  integration,
  searchText,
  isLast,
}: {
  integration: IntegrationRow;
  searchText: string;
  isLast: boolean;
}) {
  return (
    <div
      key={integration.id}
      className={cn(
        "flex flex-wrap items-center justify-between gap-2 border border-stroke-table border-b-0 p-2 lg:flex-nowrap dark:border-stroke-line",
        isLast && "border-b",
      )}
    >
      <div className="flex w-full min-w-24 items-center gap-2">
        <ControlIcon
          control={integration.control}
          className="text-secondary-foreground"
        />
        <div className="flex flex-wrap items-baseline gap-2">
          <Highlight text={integration.controlName} highlight={searchText} />
          {integration.connected && (
            <div className="flex items-center gap-2">
              <Highlight
                text={integration.name}
                highlight={searchText}
                className="text-secondary-foreground text-xs"
              />
              <EditIntegrationDropdown
                instanceId={integration.id}
                control={integration.control}
                name={integration.name}
                className="self-center"
              />
            </div>
          )}
        </div>
      </div>
      <div className="flex w-full flex-wrap items-center gap-4">
        <DocumentationLink control={integration.control} />
        <div className="flex flex-wrap items-center gap-2">
          {integration.connected ? (
            <>
              <DisconnectButton
                instanceId={integration.id}
                control={integration.control}
                integrationName={integration.name}
              />
              <SettingsButton
                instanceId={integration.id}
                control={integration.control}
                integrationName={integration.name}
              />
              {integration.hasGroups && (
                <GroupsButton
                  instanceId={integration.id}
                  control={integration.control}
                  integrationName={integration.name}
                />
              )}
            </>
          ) : (
            <ConnectButton
              control={integration.control}
              disabled={!getIntegrationForm(integration.control)}
              integrationName={integration.name}
            />
          )}
        </div>
        <Connection connected={integration.connected} />
      </div>
    </div>
  );
}

function IntegrationGridItem({
  integration,
  searchText,
}: {
  integration: IntegrationRow;
  searchText: string;
}) {
  return (
    <div
      key={integration.id}
      className={cn(
        "flex flex-col gap-4 border p-4",
        integration.connected
          ? "border-status-green"
          : "border-stroke-table dark:border-stroke-line",
      )}
    >
      <div className="flex flex-col gap-2 pb-4">
        <div className="flex justify-between">
          <ControlIcon
            control={integration.control}
            className="h-10 w-10 text-secondary-foreground"
          />
        </div>
        <div className="flex flex-col flex-wrap items-start gap-2 lg:flex-row lg:items-center">
          <Highlight
            text={getControlName(integration.control)}
            highlight={searchText}
            className="text-xl"
          />
          {integration.connected && (
            <div className="flex items-center gap-2">
              <Highlight
                text={integration.name}
                highlight={searchText}
                className="text-secondary-foreground text-sm"
              />
              <EditIntegrationDropdown
                instanceId={integration.id}
                control={integration.control}
                name={integration.name}
              />
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Connection connected={integration.connected} />
          {integration.connected ? (
            <DisconnectButton
              instanceId={integration.id}
              control={integration.control}
              integrationName={integration.name}
            />
          ) : (
            <ConnectButton
              control={integration.control}
              disabled={!getIntegrationForm(integration.control)}
              integrationName={integration.name}
            />
          )}
        </div>
      </div>
      <div className="mt-auto flex items-center gap-2 border-stroke-table border-t pt-4 dark:border-stroke-line">
        <DocumentationLink control={integration.control} />
        {integration.connected && (
          <>
            <SettingsButton
              instanceId={integration.id}
              control={integration.control}
              integrationName={integration.name}
            />
            {integration.hasGroups && (
              <GroupsButton
                instanceId={integration.id}
                control={integration.control}
                integrationName={integration.name}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

const ConnectButton = ({
  control,
  disabled,
  integrationName,
}: {
  control: ValidControlCode;
  disabled?: boolean;
  integrationName: string;
}) => (
  <Link
    to={window.location.pathname}
    search={(prev) => ({
      ...prev,
      overlay: ["integration", { control }],
    })}
    params={(prev) => prev}
    disabled={disabled}
  >
    <Button
      icon={<PlusIcon className="text-secondary-foreground" />}
      intent="pill"
      size="tiny"
      disabled={disabled}
      className={cn(tb(`connect-${integrationName || "unnamed"}`))}
    >
      Connect
    </Button>
  </Link>
);

const DisconnectButton = ({
  instanceId,
  control,
  integrationName,
}: {
  instanceId: string;
  control: ControlCode;
  integrationName: string;
}) => {
  const authConfig = useAuthConfig();
  const name = getControlName(control);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const { mutate: disconnect, isPending: isDisconnecting } = useMutation({
    mutationFn: async () => {
      await service(authConfig).partner.detachPartner(
        getEnumName(ControlCodes, control),
        instanceId,
      );
    },
    onSuccess: async () => {
      notifySuccess({ message: `Disconnected from ${name}.` });
      setShowConfirmation(false);
    },
    onError: () => {
      notifyError({ message: `Failed to disconnect from ${name}.` });
      setShowConfirmation(false);
    },
  });

  return (
    <>
      <Button
        icon={<UnlinkIcon className="text-secondary-foreground" />}
        loading={isDisconnecting}
        onClick={() => setShowConfirmation(true)}
        intent="pill"
        size="tiny"
        className={tb(`disconnect-${integrationName || "unnamed"}`)}
      >
        Disconnect
      </Button>

      <Dialog open={showConfirmation} onOpenChange={setShowConfirmation}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Disconnection</DialogTitle>
          </DialogHeader>
          <DialogBody>
            Are you sure you want to disconnect from {name}?
          </DialogBody>
          <DialogFooter>
            <DialogClose asChild>
              <Button intent="secondary">Cancel</Button>
            </DialogClose>
            <Button
              intent="negative"
              loading={isDisconnecting}
              onClick={() => disconnect()}
            >
              Disconnect
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

const Connection: React.FC<{ connected: boolean; className?: string }> = ({
  connected,
  className,
}) => {
  if (connected) {
    return (
      <span
        className={cn("flex items-center gap-1 text-status-green", className)}
      >
        <CheckmarkIcon />
        Connected
      </span>
    );
  }
  return (
    <span className={cn("flex items-center gap-1 text-status-red", className)}>
      <CloseIcon />
      Not connected
    </span>
  );
};

const SettingsButton: React.FC<{
  instanceId: string;
  control: ValidControlCode;
  integrationName: string;
}> = ({ instanceId, control, integrationName }) => {
  const overlay = useOverlay();

  return (
    <Button
      icon={<SettingsIcon className="text-secondary-foreground" />}
      intent="pill"
      size="tiny"
      onClick={() => overlay.open("integration", { instanceId, control })}
      className={cn(tb(`settings-${integrationName || "unnamed"}`))}
    >
      Settings
    </Button>
  );
};

const GroupsButton: React.FC<{
  instanceId: string;
  control: ValidControlCode;
  integrationName: string;
}> = ({ instanceId, control, integrationName }) => {
  const overlay = useOverlay();

  return (
    <Button
      icon={<SettingsIcon className="text-secondary-foreground" />}
      intent="pill"
      size="tiny"
      onClick={() =>
        overlay.open("integration", { instanceId, control, view: "groups" })
      }
      className={cn(tb(`settings-${integrationName || "unnamed"}`))}
    >
      Groups
    </Button>
  );
};

const AddIntegrationButton = ({
  onClick,
  ...props
}: Omit<ButtonProps, "onClick"> & {
  onClick: (control: ValidControlCode) => void;
}) => {
  const authConfig = useAuthConfig();
  const { data: integrations } =
    integrationsQuery(authConfig).useSuspenseQuery();

  const options = controlOptions
    .filter(({ id }) => integrations.some((i) => i.control === id))
    .filter(({ id }) => getIntegrationForm(id))
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <DropdownSelect
      {...props}
      disabled={options.length === 0}
      options={options}
      placeholder="Search integrations"
      getLabel={(o) => o.name}
      getValue={(o) => o.id}
      selection={[]}
      onChangeSelection={onClick}
      renderOption={({ option, label }) => (
        <div className="flex items-center gap-2">
          <ControlIcon
            control={option.id}
            className="text-secondary-foreground"
          />
          {label}
        </div>
      )}
    />
  );
};

const DOCUMENTATION_URLS: Record<ValidControlCode, string | null> = {
  [ControlCodes.AWS_SSM]: "https://support.preludesecurity.com/docs/amazon-ssm", // TODO: add real doc link
  [ControlCodes.AZURE_VM]:
    "https://support.preludesecurity.com/connecting-azure-vm-discovery-integration",
  [ControlCodes.CROWDSTRIKE]:
    "https://support.preludesecurity.com/docs/crowdstrike",
  [ControlCodes.CROWDSTRIKE_IDENTITY]: "https://support.preludesecurity.com/", // TODO: add real doc link
  [ControlCodes.DEFENDER]:
    "https://support.preludesecurity.com/docs/microsoft-defender-1",
  [ControlCodes.DEFENDER_DISCOVERY]:
    "https://support.preludesecurity.com/docs/microsoft-defender-1",
  [ControlCodes.EC2]: "https://support.preludesecurity.com/docs/amazon-ec2",
  [ControlCodes.ENTRA]: "https://support.preludesecurity.com/docs/entra-id",
  [ControlCodes.GITHUB]: "https://support.preludesecurity.com/docs/github",
  [ControlCodes.GMAIL]: "https://support.preludesecurity.com/docs/gmail",
  [ControlCodes.GOOGLE_IDENTITY]:
    "https://support.preludesecurity.com/docs/google-identity",
  [ControlCodes.INTUNE]:
    "https://support.preludesecurity.com/docs/microsoft-intune",
  [ControlCodes.JAMF]: "https://support.preludesecurity.com/docs/jamf",
  [ControlCodes.M365]:
    "https://support.preludesecurity.com/docs/microsoft-m365-integration",
  [ControlCodes.OKTA]: "https://support.preludesecurity.com/docs/okta-1",
  [ControlCodes.QUALYS]:
    "https://support.preludesecurity.com/connecting-qualys",
  [ControlCodes.QUALYS_DISCOVERY]:
    "https://support.preludesecurity.com/connecting-qualys",
  [ControlCodes.RAPID7]: "https://support.preludesecurity.com", // TODO: add real doc link
  [ControlCodes.RAPID7_DISCOVERY]: "https://support.preludesecurity.com", // TODO: add real doc link
  [ControlCodes.INTUNE_HOST_FIREWALL]: "https://support.preludesecurity.com", // TODO: add real doc link
  [ControlCodes.S3]: "https://support.preludesecurity.com/docs/amazon-s3",
  [ControlCodes.SENTINELONE]:
    "https://support.preludesecurity.com/docs/sentinelone",
  [ControlCodes.SERVICENOW]:
    "https://support.preludesecurity.com/docs/servicenow",
  [ControlCodes.SPLUNK]: "https://support.preludesecurity.com/docs/splunk",
  [ControlCodes.VECTR]: "https://support.preludesecurity.com/docs/vectr",
  [ControlCodes.TENABLE]:
    "https://support.preludesecurity.com/connecting-tenable",
  [ControlCodes.TENABLE_DISCOVERY]: "https://support.preludesecurity.com", // TODO: add real doc link
};

const DocumentationLink: React.FC<{ control: ValidControlCode }> = ({
  control,
}) => {
  const link = DOCUMENTATION_URLS[control];

  const button = (
    <Button
      intent="pill"
      size="tiny"
      icon={<BookIcon className="text-secondary-foreground" />}
      className={cn(tb(`docs-${getControlName(control) || "unnamed"}`))}
      disabled={!link}
    >
      Docs
    </Button>
  );

  if (!link) {
    return button;
  }

  return (
    <a target="_blank" href={link} rel="noreferrer">
      {button}
    </a>
  );
};
